import React, { useRef, useState, useMemo, useEffect } from 'react';

const Home = ({ setCurrentPage, profiles, allTracks, setActiveArtist }) => {
  // --- STATES (Immer ganz oben) ---
  const [showManifest, setShowManifest] = useState(false);
  const [showFullManifest, setShowFullManifest] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // --- REFS ---
  const trackRef = useRef(null);
  const startX = useRef(0);
  const walkX = useRef(0);
  const totalOffset = useRef(0);
  const velocity = useRef(0);
  const lastTimestamp = useRef(0);
  const lastX = useRef(0);
  const requestRef = useRef(null);

  // --- DATA PREPARATION ---
  const displayMode = "avatar"; 

  const validProfiles = useMemo(() => {
    return profiles.filter(artist => {
      if (displayMode === "avatar") return !!artist.avatar;
      if (displayMode === "cover") return !!artist.cover;
      return !!artist.avatar || !!artist.cover;
    });
  }, [profiles]);

  const tripledItems = useMemo(() => {
    let items = [];
    validProfiles.forEach(artist => {
      if (displayMode === "avatar" || displayMode === "both") {
        if (artist.avatar) items.push({ ...artist, displayImg: artist.avatar, type: 'avatar' });
      }
      if (displayMode === "cover" || displayMode === "both") {
        if (artist.cover) items.push({ ...artist, displayImg: artist.cover, type: 'cover' });
      }
    });
    return [...items, ...items, ...items];
  }, [validProfiles]);

  // INITIAL POSITION
  useEffect(() => {
    if (trackRef.current && tripledItems.length > 0) {
      const timer = setTimeout(() => {
        const oneThird = trackRef.current.scrollWidth / 3;
        totalOffset.current = -oneThird;
        trackRef.current.style.transform = `translateX(${-oneThird}px)`;
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [tripledItems.length]);

  if (!profiles || profiles.length === 0) return null;

  // --- SLIDER LOGIK (Unverändert) ---
  const applyInertia = () => {
    if (Math.abs(velocity.current) > 0.5) {
      velocity.current *= 0.95;
      totalOffset.current += velocity.current;
      if (trackRef.current) {
        const oneThird = trackRef.current.scrollWidth / 3;
        if (totalOffset.current < -oneThird * 2) totalOffset.current += oneThird;
        if (totalOffset.current > -oneThird) totalOffset.current -= oneThird;
        trackRef.current.style.transform = `translateX(${totalOffset.current}px)`;
      }
      requestRef.current = requestAnimationFrame(applyInertia);
    } else {
      cancelAnimationFrame(requestRef.current);
    }
  };

  const startDragging = (e, clientX) => {
    if (e.type === 'mousedown') e.preventDefault();
    cancelAnimationFrame(requestRef.current);
    setIsDragging(true);
    startX.current = clientX;
    lastX.current = clientX;
    lastTimestamp.current = Date.now();
    walkX.current = 0;
  };

  const moveDragging = (clientX) => {
    if (!isDragging || !trackRef.current) return;
    const now = Date.now();
    const dt = now - lastTimestamp.current;
    if (dt > 0) velocity.current = (clientX - lastX.current);
    const diff = (clientX - startX.current) * 1.5;
    lastX.current = clientX;
    lastTimestamp.current = now;
    let currentPos = totalOffset.current + diff;
    const oneThird = trackRef.current.scrollWidth / 3;
    if (currentPos < -oneThird * 2) {
      totalOffset.current += oneThird;
      startX.current += oneThird / 1.5;
      currentPos += oneThird;
    } else if (currentPos > -oneThird) {
      totalOffset.current -= oneThird;
      startX.current -= oneThird / 1.5;
      currentPos -= oneThird;
    }
    requestAnimationFrame(() => {
      if (trackRef.current && isDragging) {
        trackRef.current.style.transform = `translateX(${currentPos}px)`;
      }
    });
    walkX.current = diff; 
  };

  const stopDragging = () => {
    if (!isDragging) return;
    setIsDragging(false);
    totalOffset.current += walkX.current;
    requestRef.current = requestAnimationFrame(applyInertia);
  };

  return (
    <div className="home-content">
      
      {/* 2. MARQUEE SLIDER */}
      <div 
        className="marquee-viewport"
        onMouseDown={(e) => startDragging(e, e.pageX)}
        onMouseMove={(e) => moveDragging(e.pageX)}
        onMouseUp={stopDragging}
        onMouseLeave={stopDragging}
        onTouchStart={(e) => startDragging(e, e.touches[0].pageX)}
        onTouchMove={(e) => moveDragging(e.touches[0].pageX)}
        onTouchEnd={stopDragging}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <div ref={trackRef} className="track-wrapper" style={{ transform: `translateX(${totalOffset.current}px)` }}>
          <div className={`marquee-track ${isDragging ? 'is-paused' : ''}`}>
            {tripledItems.map((item, index) => (
              <div key={`${item.uid || index}-${index}`} className="artist-portrait-container" onClick={() => Math.abs(walkX.current) < 10 && setActiveArtist(item)}>
                <img src={item.displayImg} className="artist-portrait" alt={item.name || "Artist"} draggable="false" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 3. MANIFEST VORSCHAU (Direkt unter Slider) */}
      <div className="manifest-intro-section">
        <button onClick={() => setShowManifest(!showManifest)} className="btn-station-grey">
          {showManifest ? '[ CLOSE ]' : '[ READ THE MANIFESTO ]'}
        </button>
        
        {showManifest && (
          <div className="manifest-mini-box">
            <p>
              Wir glauben an die rohe Energie der Straße, an ungeschliffene Diamanten und den Herzschlag der Stadt. 
              Diese Plattform ist kein Algorithmus. Sie ist ein lebendiges Archiv. 
              Hier zählt nur der Vibe, nicht der Follower-Count.
            </p>
            <button onClick={() => setShowFullManifest(true)} className="btn-read-full">
              READ FULL MANIFESTO {'>'}
            </button>
          </div>
        )}
      </div>

      {/* 4. CTA SECTION */}
      <div className="home-footer">
        <h2 className="cta-title">Ready to dive in?</h2>
        <button onClick={() => setCurrentPage("radio")} className="btn-main-action">
          START RADIO
        </button>
      </div>

      {/* 5. FULL MANIFESTO OVERLAY */}
      {showFullManifest && (
        <div className="full-manifest-overlay">
          <button className="close-overlay" onClick={() => setShowFullManifest(false)}>×</button>
          
          <div className="manifest-content-scroll">
            <h2 className="manifest-subtitle">Streetwise Radio</h2>
            <h1 className="manifest-title">Das Manifest</h1>
            
            <div className="manifest-full-text">
              <p>Streetwise Radio ist kein Label. Keine Bühne für Perfektion.</p>
              <p>Es ist ein Ort für echte Menschen, echte Geschichten, echten Ausdruck.</p>
              <p>Jeder Track ist ein künstlerischer Ausdruck. Eine ungehörte Stimme. Eine Geschichte die es nirgendwo sonst gibt.</p>
              <p>Wir sind regional. Wir sind familiär. Wir sind eine Community von und für Musicians die lieber spielen als promoten. Die lieber teilen als verkaufen. Die lieber zuhören als sich zu vergleichen.</p>
              <p>Hier kannst du einen Track hochladen der gestern Nacht entstanden ist. Hier kannst du Musik hören die du nirgendwo sonst findest. Und hier kannst du Menschen kennenlernen die genauso fühlen wie du.</p>
              <p>Streetwise Radio ist der Soundcheck bevor der Rest der Welt zuhört.</p>
              
              <div className="manifest-highlight-box">
                <p>Roh. Echt. Unverwechselbar.</p>
                <p>Mach mit. Lad hoch. Hör rein. Verbinde dich.</p>
                <p>Das Studio gehört euch allen.</p>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Home;